import { motion } from "framer-motion";
import { ArrowRight, FileText, ExternalLink, Download, BookOpen } from "lucide-react";
import { ScreenShell, GlassCard, GradientText, ShimmerButton, SimHeader } from "@/components/sim/shared";
import { MicroParticles } from "@/components/sim/decor";

interface Props {
  sequenceNo: number;
  totalDecisions: number;
  xp: number;
  timerDisplay: string | undefined;
  documentUrl: string;
  documentTitle: string | null;
  onContinue: () => void;
}

/**
 * DocumentScreen — Lovable wrapper around the pre-decision document iframe.
 * Replaces the raw-PDF-in-dark-theme jarring contrast with a dark-framed
 * branded viewer: doc title strip above the iframe, toolbar with Download +
 * Open-in-new-tab, gentle "take your time" note. The iframe itself still
 * shows the document via the browser's native viewer (no PDF.js dep) — we
 * just make the surround feel intentional.
 */
export function DocumentScreen({
  sequenceNo,
  totalDecisions,
  xp,
  timerDisplay,
  documentUrl,
  documentTitle,
  onContinue,
}: Props): JSX.Element {
  return (
    <ScreenShell>
      <SimHeader decision={sequenceNo} xp={xp} timerDisplay={timerDisplay} />
      <main className="relative mx-auto max-w-5xl px-4 pb-12 md:px-8">
        <MicroParticles />

        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}>
          {/* Header */}
          <div className="mb-5 flex flex-wrap items-end justify-between gap-3">
            <div>
              <div className="flex items-center gap-2 text-cyan-300">
                <FileText className="h-4 w-4" />
                <span className="text-[10px] font-bold uppercase tracking-[0.18em]">Review first</span>
              </div>
              <h1 className="mt-2 text-[26px] font-bold leading-tight">
                <GradientText>{documentTitle ?? "Review this document"}</GradientText>
              </h1>
              <p className="mt-2 inline-flex items-center gap-1.5 text-sm text-white/55">
                <BookOpen className="h-3.5 w-3.5" />
                Take your time — we&rsquo;ll wait for you to finish before the decision.
              </p>
            </div>
            <span className="rounded-full border border-white/10 bg-white/[0.04] px-3 py-1 text-[10px] font-bold uppercase tracking-[0.15em] text-white/55">
              Decision {sequenceNo} of {totalDecisions}
            </span>
          </div>

          {/* Document viewer — dark frame around the iframe, toolbar in the frame */}
          <GlassCard className="overflow-hidden p-0">
            <div className="flex items-center justify-between border-b border-white/10 bg-white/[0.03] px-4 py-2.5">
              <span className="inline-flex items-center gap-2 text-[11px] font-medium text-white/65">
                <FileText className="h-3.5 w-3.5" />
                {documentTitle ?? "Document"}
              </span>
              <div className="flex items-center gap-2">
                <a
                  href={documentUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 rounded-lg border border-white/10 bg-white/5 px-3 py-1.5 text-[11px] font-medium text-white/75 transition-colors hover:border-white/25 hover:bg-white/10 hover:text-white"
                  title="Open in a new tab"
                >
                  <ExternalLink className="h-3 w-3" /> Open
                </a>
                <a
                  href={documentUrl}
                  download
                  className="inline-flex items-center gap-1.5 rounded-lg border border-white/10 bg-white/5 px-3 py-1.5 text-[11px] font-medium text-white/75 transition-colors hover:border-white/25 hover:bg-white/10 hover:text-white"
                  title="Download"
                >
                  <Download className="h-3 w-3" /> Download
                </a>
              </div>
            </div>
            <div className="h-[65vh] w-full bg-white">
              <iframe
                src={documentUrl}
                title={documentTitle ?? "Document"}
                className="h-full w-full"
              />
            </div>
          </GlassCard>

          {/* Continue */}
          <div className="mt-7 flex justify-end">
            <ShimmerButton onClick={onContinue}>
              I&rsquo;ve reviewed it — continue <ArrowRight className="h-4 w-4" />
            </ShimmerButton>
          </div>
        </motion.div>
      </main>
    </ScreenShell>
  );
}
